import { Analysis, AnalysisItem, AnalysisFormData, BackgroundAnalysisOptions } from '../types';
import { analyzeWithAI } from './aiService';
import { getCsrfToken, resetCsrfToken, getCurrentCsrfToken } from './csrfService';

const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

// Интерфейс для расширения анализа
interface ExpandAnalysisRequest {
  analysisId: string; // external_id анализа
  plusCount: number;
  minusCount: number;
  currentAnalysis?: Analysis; // актуальные данные анализа с дополнительными аргументами
}

interface ExpandAnalysisResponse {
  success: boolean;
  newItems: AnalysisItem[];
  expansionStats: {
    plusCount: number;
    minusCount: number;
    totalNew: number;
  };
}

// Основная функция анализа с ИИ
export async function analyzeData(
  data: AnalysisFormData, 
  options?: BackgroundAnalysisOptions,
  abortSignal?: AbortSignal
): Promise<Analysis> {
  return await analyzeWithAI(data, options, abortSignal);
}



// @ts-ignore - Функция может понадобиться как fallback
function generatePlusItems(_description: string): AnalysisItem[] {
  const plusTemplates = [
    { text: 'Улучшение качества жизни', rating: 4 },
    { text: 'Экономия времени', rating: 3 },
    { text: 'Повышение эффективности', rating: 4 },
    { text: 'Долгосрочная выгода', rating: 5 },
    { text: 'Инновационное решение', rating: 3 },
    { text: 'Позитивное влияние на будущее', rating: 4 },
    { text: 'Соответствие целям', rating: 4 },
    { text: 'Высокое качество', rating: 4 },
    { text: 'Надежность', rating: 3 },
    { text: 'Универсальность', rating: 3 }
  ];
  
  // Выбираем случайные плюсы
  const selected = plusTemplates
    .sort(() => Math.random() - 0.5)
    .slice(0, Math.floor(Math.random() * 4) + 3);
  
  return selected.map((item, index) => ({
    id: `plus-${index}`,
    text: item.text,
    rating: item.rating,
    category: 'plus' as const
  }));
}

// @ts-ignore - Функция может понадобиться как fallback
function generateMinusItems(_description: string): AnalysisItem[] {
  const minusTemplates = [
    { text: 'Финансовые затраты', rating: 4 },
    { text: 'Временные затраты', rating: 3 },
    { text: 'Риски и неопределенность', rating: 4 },
    { text: 'Сложность реализации', rating: 3 },
    { text: 'Возможные негативные последствия', rating: 4 },
    { text: 'Необходимость изменений', rating: 3 },
    { text: 'Зависимость от внешних факторов', rating: 3 },
    { text: 'Потенциальные ограничения', rating: 2 },
    { text: 'Необходимость обучения', rating: 2 },
    { text: 'Возможные конфликты интересов', rating: 3 }
  ];
  
  // Выбираем случайные минусы
  const selected = minusTemplates
    .sort(() => Math.random() - 0.5)
    .slice(0, Math.floor(Math.random() * 4) + 2);
  
  return selected.map((item, index) => ({
    id: `minus-${index}`,
    text: item.text,
    rating: item.rating,
    category: 'minus' as const
  }));
}

// @ts-ignore - Функция может понадобиться как fallback
function generateRecommendation(plusScore: number, minusScore: number, _description: string): string {
  const difference = plusScore - minusScore;
  
  if (difference > 5) {
    return 'Сильная рекомендация ДА. Плюсы значительно перевешивают минусы. Это решение принесет существенную пользу и стоит серьезно рассмотреть возможность реализации.';
  } else if (difference > 0) {
    return 'Рекомендация ДА. Плюсы перевешивают минусы, но стоит внимательно проанализировать детали и возможные риски перед принятием окончательного решения.';
  } else if (difference === 0) {
    return 'Нейтральная рекомендация. Плюсы и минусы уравновешены. Рекомендуется собрать дополнительную информацию или рассмотреть альтернативные варианты.';
  } else if (difference > -5) {
    return 'Рекомендация НЕТ. Минусы перевешивают плюсы, но разница не критична. Стоит рассмотреть возможность улучшения условий или поиска альтернатив.';
  } else {
    return 'Сильная рекомендация НЕТ. Минусы значительно перевешивают плюсы. Рекомендуется отказаться от этого варианта и рассмотреть альтернативные решения.';
  }
}

// @ts-ignore - Функция может понадобиться как fallback
function generateTitleFromDescription(description: string): string {
  // Простая логика генерации названия на основе ключевых слов в описании
  const words = description.toLowerCase().split(/\s+/);
  const keyWords = words.filter(word => 
    word.length > 3 && 
    !['что', 'как', 'где', 'когда', 'почему', 'какой', 'какая', 'какие', 'это', 'для', 'при', 'над', 'под', 'между', 'внутри', 'вне', 'через', 'по', 'из', 'от', 'до', 'без', 'с', 'на', 'в', 'о', 'об', 'у', 'к', 'за', 'перед', 'после', 'во', 'со', 'ко', 'обо', 'не', 'ни', 'бы', 'же', 'ли', 'то', 'так', 'вот', 'тут', 'там', 'здесь', 'туда', 'сюда', 'оттуда', 'отсюда', 'теперь', 'сейчас', 'потом', 'раньше', 'позже', 'всегда', 'никогда', 'иногда', 'часто', 'редко', 'все', 'вся', 'всех', 'всем', 'всеми', 'каждый', 'любой', 'всякий', 'некоторый', 'несколько', 'много', 'мало', 'больше', 'меньше', 'столько', 'сколько', 'такой', 'такая', 'такие', 'такое', 'этот', 'эта', 'эти', 'это', 'тот', 'та', 'те', 'то'].includes(word)
  );
  
  if (keyWords.length === 0) {
    return 'Анализ решения';
  }
  
  // Берем первые 3-4 ключевых слова и формируем название
  const titleWords = keyWords.slice(0, 4);
  const title = titleWords
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return title || 'Анализ решения';
}

// Функция для расширения анализа
export async function expandAnalysis(request: ExpandAnalysisRequest): Promise<ExpandAnalysisResponse> {
  try {
    // Получаем CSRF токен
    let csrfToken = getCurrentCsrfToken();
    if (!csrfToken) {
      console.log('CSRF: Токен не найден, запрашиваем новый');
      csrfToken = await getCsrfToken();
    } else {
      console.log('CSRF: Используем существующий токен из кэша');
    }

    const response = await fetch(`${SHARE_SERVER_URL}/api/expand-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
              body: JSON.stringify({
          externalId: request.analysisId, // analysisId содержит external_id
          plusCount: request.plusCount,
          minusCount: request.minusCount,
          currentAnalysis: request.currentAnalysis // отправляем актуальные данные анализа
        }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Если ошибка CSRF, сбрасываем токен и пробуем еще раз
      if (errorData.code === 'CSRF_ERROR') {
        resetCsrfToken();
        throw new Error('Ошибка CSRF токена. Попробуйте еще раз.');
      }
      
      throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // Если это ошибка получения CSRF токена, пробуем без него
    if (error instanceof Error && error.message.includes('CSRF')) {
      console.warn('CSRF токен недоступен, пробуем без защиты');
      
      const response = await fetch(`${SHARE_SERVER_URL}/api/expand-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          externalId: request.analysisId,
          plusCount: request.plusCount,
          minusCount: request.minusCount,
          currentAnalysis: request.currentAnalysis
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ошибка: ${response.status}`);
      }

      const data = await response.json();
      return data;
    }
    
    console.error('Error expanding analysis:', error);
    throw error;
  }
} 